import React from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { BriefField, ReadinessResponse } from '../../types/aai';
import { AAI_DIMENSIONS, DIMENSION_LABELS } from '../../types/aai';
import { Action, Chip, Drawer, EmptyState, ErrorNote, Kicker, Loading } from '../../components/aa';
import { Tiles } from '../owner/ControlDrawers';

/*
 * My Readiness, in a drawer over whichever Home opened it.
 *
 * The person's own five dimensions as ranges, never exact scores, and the
 * facts they gave at onboarding. It is private growth detail, so it stays a
 * layer over the page they were on rather than a destination of its own.
 * Both the employee and the department head open the same drawer.
 */

export const STAGE_LABEL: Record<string, string> = {
  nascent: 'Starting out',
  early: 'Early',
  developing: 'Developing',
  mature: 'Mature',
  advanced: 'Advanced',
};

const percent = (n: number): number => Math.round(Math.max(0, Math.min(1, n)) * 100);

export const measuredLabel = (iso: string | null | undefined): string => {
  if (!iso) return 'baseline recorded';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'baseline recorded';
  return `measured ${when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
};

export const DimensionBar: React.FC<{ label: string; range?: { low: number; high: number } }> = ({ label, range }) => {
  let reading = 'No signal yet';
  if (range) reading = `${percent(range.low)}–${percent(range.high)}%`;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12.5px] font-semibold text-ink">{label}</span>
        <span className="font-sans text-[12px] tabular-nums text-ink-muted">{reading}</span>
      </div>
      <div className="relative mt-2 h-2 w-full overflow-hidden rounded-full bg-canvas-inset">
        {range && (
          <div
            className="absolute h-2 rounded-full bg-ink"
            style={{ left: `${percent(range.low)}%`, width: `${percent(range.high) - percent(range.low)}%` }}
          />
        )}
      </div>
    </div>
  );
};

const factValue = (field: BriefField): string => {
  const v = field.value;
  if (Array.isArray(v)) return v.map(String).join(', ') || 'Not given';
  if (v === null || v === undefined || v === '') return 'Not given';
  return String(v);
};

export const ReadinessDrawer: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const mine = useAsync<ReadinessResponse>(() => aaiApi.getMyReadiness(), 'Could not load your readiness.');
  const readiness = mine.data?.readiness || null;
  const fields = Object.values(mine.data?.brief?.fields || {});

  let badge: React.ReactNode = <Chip tone="private">Private</Chip>;
  if (readiness) badge = <Chip>{STAGE_LABEL[readiness.stage] || readiness.stage}</Chip>;
  let sub = 'Your growth with AI. Shown as ranges, not exact scores, and never a leaderboard.';
  if (readiness) sub = `Shown as ranges, not exact scores · ${measuredLabel(readiness.measured_at)} · private to you`;

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>My readiness</Kicker>}
      badge={badge}
      title="Your AI readiness"
      sub={sub}
      footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
    >
      {mine.loading && <Loading label="Loading your readiness" />}
      {mine.error && <ErrorNote message={mine.error} />}
      {!mine.loading && !mine.error && !readiness && (
        <EmptyState
          title="No readiness baseline yet"
          reason="A short Orbit conversation about how you work today sets it. Your answers stay yours."
          action={<Action size="sm" arrow to="/onboarding">Start onboarding</Action>}
        />
      )}
      {readiness && (
        <>
          <div className="grid gap-5">
            {AAI_DIMENSIONS.map((dim) => (
              <DimensionBar key={dim} label={DIMENSION_LABELS[dim]} range={readiness.dimensions?.[dim] || undefined} />
            ))}
          </div>
          {fields.length > 0 && (
            <div className="mt-8">
              <Kicker className="mb-3">How you work</Kicker>
              <Tiles facts={fields.map((f) => ({ label: f.label, value: factValue(f) }))} />
            </div>
          )}
          <p className="m-0 mt-6 text-[12.5px] leading-[20px] text-ink-dim">
            Only you see this detail. Managers see protected aggregates for groups large enough to report.
          </p>
        </>
      )}
    </Drawer>
  );
};

export default ReadinessDrawer;
